import React, { useMemo } from 'react';
import {
  Box,
  Text,
  Stack,
  Portal,
  Transition,
} from '@mantine/core';
import {
  IconUpload,
  IconFileSearch,
  IconMapPin,
  IconCheck,
  IconX,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import type { ScanStep } from '@/hooks/useScanner';

import classes from './scanProgressOverlay.module.css';

// ============================================
// Types
// ============================================

export interface ScanProgressOverlayProps {
  /** Whether the overlay is visible */
  opened: boolean;
  /** Current scanning step */
  step: ScanStep;
  /** Error message to display when step is 'error' */
  errorMessage?: string | null;
  /** Called when the user taps to dismiss after completion or error */
  onDismiss?: () => void;
}

// ============================================
// Step Configuration
// ============================================

interface StepConfig {
  icon: React.FC<{ size?: number; className?: string; style?: React.CSSProperties }>;
  labelKey: string;
  defaultLabel: string;
  iconClassName: string;
}

const STEP_CONFIGS: Record<Exclude<ScanStep, 'idle'>, StepConfig> = {
  uploading: {
    icon: IconUpload,
    labelKey: 'scanProgress.uploading',
    defaultLabel: 'Uploading image...',
    iconClassName: classes.animPulse,
  },
  analyzing: {
    icon: IconFileSearch,
    labelKey: 'scanProgress.analyzing',
    defaultLabel: 'Analyzing receipt...',
    iconClassName: classes.animPulse,
  },
  detecting_location: {
    icon: IconMapPin,
    labelKey: 'scanProgress.detectingLocation',
    defaultLabel: 'Detecting location...',
    iconClassName: classes.animPinBounce,
  },
  complete: {
    icon: IconCheck,
    labelKey: 'scanProgress.complete',
    defaultLabel: 'Receipt recognized!',
    iconClassName: classes.animCheckPop,
  },
  error: {
    icon: IconX,
    labelKey: 'scanProgress.error',
    defaultLabel: 'Recognition failed',
    iconClassName: classes.animFadeInUp,
  },
};

// ============================================
// Sub-components
// ============================================

/** Animated receipt icon with scanning line for the analyzing step */
const ScanAnimation: React.FC = () => {
  const lineOffsets = [20, 35, 50, 65, 78];

  return (
    <Box className={classes.receiptOutline}>
      {/* Receipt text lines */}
      {lineOffsets.map((top, i) => (
        <Box
          key={top}
          className={classes.receiptLine}
          style={{
            top: `${top}%`,
            right: i === lineOffsets.length - 1 ? '40%' : '15%',
          }}
        />
      ))}

      {/* Scanning line */}
      <Box className={classes.scanLine} />
    </Box>
  );
};

/** Step progress indicator dots */
const StepIndicator: React.FC<{ step: ScanStep }> = ({ step }) => {
  const steps: Exclude<ScanStep, 'idle' | 'error'>[] = [
    'uploading',
    'analyzing',
    'detecting_location',
    'complete',
  ];

  const currentIndex = steps.indexOf(step as typeof steps[number]);

  // Don't show for error or idle
  if (step === 'idle' || step === 'error') {
    return null;
  }

  return (
    <Box className={classes.stepIndicator}>
      {/* Show dots for all steps except 'complete' (which is the final state) */}
      {steps.slice(0, -1).map((s, index) => {
        const isComplete = index < currentIndex;
        const isCurrent = index === currentIndex;

        return (
          <Box
            key={s}
            className={`${classes.stepDot} ${isComplete ? classes.stepDotComplete : ''} ${isCurrent ? classes.stepDotCurrent : ''}`}
          />
        );
      })}
    </Box>
  );
};

// ============================================
// Main Component
// ============================================

export const ScanProgressOverlay: React.FC<ScanProgressOverlayProps> = ({
  opened,
  step,
  errorMessage,
  onDismiss,
}) => {
  const { t } = useTranslation(['dashboard', 'common']);

  const config = useMemo(() => {
    if (step === 'idle') return null;
    return STEP_CONFIGS[step];
  }, [step]);

  const isTerminal = step === 'complete' || step === 'error';
  const isAnalyzing = step === 'analyzing';

  if (!opened || !config) {
    return null;
  }

  const IconComponent = config.icon;

  return (
    <Portal>
      <Transition mounted={opened} transition="fade" duration={250}>
        {(styles) => (
          <Box
            className={classes.overlay}
            style={styles}
            onClick={isTerminal ? onDismiss : undefined}
          >
            <Stack align="center" gap="xl" className={classes.content}>
              {/* Step indicator dots */}
              <StepIndicator step={step} />

              {/* Animated icon area */}
              <Box className={classes.iconArea}>
                {/* Receipt scan animation during analyzing */}
                {isAnalyzing && (
                  <Box className={classes.scanAnimationWrapper}>
                    <ScanAnimation />
                  </Box>
                )}

                {/* Step icon (hidden during analyzing — ScanAnimation replaces it) */}
                {!isAnalyzing && (
                  <Box
                    className={`${classes.iconCircle} ${config.iconClassName} ${step === 'complete' ? classes.iconCircleSuccess : ''
                      } ${step === 'error' ? classes.iconCircleError : ''}`}
                  >
                    <IconComponent
                      size={step === 'complete' || step === 'error' ? 36 : 28}
                      style={{ color: 'white' }}
                    />
                  </Box>
                )}
              </Box>

              {/* Step label */}
              <Box className={classes.animFadeInUp}>
                <Text size="lg" fw={600} c="white" ta="center">
                  {step === 'error' && errorMessage
                    ? errorMessage
                    : t(`dashboard:${config.labelKey}`, config.defaultLabel)}
                </Text>
              </Box>

              {/* Dismiss hint for terminal states */}
              {isTerminal && onDismiss && (
                <Text
                  size="xs"
                  c="dimmed"
                  ta="center"
                  className={classes.dismissHint}
                >
                  {t('common:actions.tapToDismiss', 'Tap to dismiss')}
                </Text>
              )}
            </Stack>
          </Box>
        )}
      </Transition>
    </Portal>
  );
};